export const FIELD_LIMITS: Record<string, { min: number; max: number }> = {
  title: { min: 5, max: 120 },
  narration: { min: 10, max: 350 },
  caption: { min: 5, max: 150 },
};

export interface BlockTypeRule {
  type: string;
  label: string;
  required: boolean;
  removable: boolean;
  addable: boolean;
  minCount?: number;
  maxCount?: number;
}

export interface FormatRules {
  blockTypes: BlockTypeRule[];
  firstBlockMustBe: string;
  lastBlockMustBe: string;
}

export interface ValidatableBlock {
  block_type: string;
  title: string;
  narration: string;
  caption: string;
}

export const FORMAT_RULES: Record<string, FormatRules> = {
  listicle: {
    blockTypes: [
      { type: "hook", label: "Hook", required: true, removable: false, addable: false },
      { type: "list_item", label: "List Item", required: false, removable: false, addable: false, minCount: 5, maxCount: 5 },
      { type: "conclusion", label: "Conclusion", required: false, removable: true, addable: true, maxCount: 1 },
      { type: "cta", label: "CTA", required: true, removable: false, addable: false },
    ],
    firstBlockMustBe: "hook",
    lastBlockMustBe: "cta",
  },
};

export function getFormatRules(slug?: string): FormatRules | null {
  if (!slug) return null;
  return FORMAT_RULES[slug] || null;
}

export function validateBlocks(blocks: ValidatableBlock[], rules: FormatRules | null): string[] {
  const errors: string[] = [];

  for (const block of blocks) {
    for (const [field, limits] of Object.entries(FIELD_LIMITS)) {
      const value = (block as any)[field] || "";
      if (value.length < limits.min) {
        errors.push(`${block.block_type.replace(/_/g, " ")} ${field}: minimum ${limits.min} characters required`);
      }
      if (value.length > limits.max) {
        errors.push(`${block.block_type.replace(/_/g, " ")} ${field}: maximum ${limits.max} characters allowed`);
      }
    }
  }

  if (!rules) return errors;

  if (blocks.length === 0) {
    errors.push("At least one block is required");
    return errors;
  }

  const counts: Record<string, number> = {};
  for (const b of blocks) {
    counts[b.block_type] = (counts[b.block_type] || 0) + 1;
  }

  for (const rule of rules.blockTypes) {
    const count = counts[rule.type] || 0;
    if (rule.required && count === 0) {
      errors.push(`${rule.label} is required`);
    }
    if (rule.addable || (rule.minCount !== undefined && count > 0)) {
      if (rule.minCount !== undefined && count < rule.minCount) {
        errors.push(`At least ${rule.minCount} ${rule.label.toLowerCase()}s required (${count} found)`);
      }
      if (rule.maxCount !== undefined && count > rule.maxCount) {
        errors.push(`Maximum ${rule.maxCount} ${rule.label.toLowerCase()}s allowed (${count} found)`);
      }
    }
  }

  if (rules.firstBlockMustBe && blocks[0].block_type !== rules.firstBlockMustBe) {
    errors.push(`First block must be ${rules.firstBlockMustBe.replace(/_/g, " ")}`);
  }
  if (rules.lastBlockMustBe && blocks[blocks.length - 1].block_type !== rules.lastBlockMustBe) {
    errors.push(`Last block must be ${rules.lastBlockMustBe.replace(/_/g, " ")}`);
  }

  return errors;
}
